import { samlQueryKeys } from '@/config/queryKeys';
import { adminRoles } from '@/constants';
import { useOrgAdminRenderGate } from '@/hooks';
import { apiClient } from '@/lib/api-client';
import { scrollbars } from '@/styles/index.module.css';
import type { SAMLMetadata, SAMLProviderFormDefaults, SAMLProviderGetResponse } from '@/types';
import { isForbiddenError } from '@/types/errors';
import RouteAccessGate from '@components/RouteAccessGate';
import { Group, Popover, Tooltip, getStackClassName, noop } from '@nautilusbio/nb-design-system';
import { Alert } from '@nautilusbio/nb-design-system/Alert';
import Button from '@nautilusbio/nb-design-system/Button';
import Input from '@nautilusbio/nb-design-system/Input';
import type { InputProps } from '@nautilusbio/nb-design-system/Input';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import Tabs, { Tab, TabList, TabPanel } from '@nautilusbio/nb-design-system/Tabs';
import Text from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import Upload from '@nautilusbio/nb-design-system/Upload';
import { useForm } from '@tanstack/react-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLoaderData, useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import { Cog, Lock } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import { CopyText, FormSectionTitle } from './components';
import { COGNITO_PROVIDER_NAME_MAX_LENGTH, buildSamlSchema, isValidUrl } from './samlSchema';
import type { SamlMetadataFile, SamlProviderFormValues } from './samlSchema';
import styles from './styles.module.css';

/** The POST takes `metadata_file` as an `UploadFile`, so re-posting a provider's existing metadata
 *  needs a filename for the part; the backend reads the bytes and ignores it. */
const EXISTING_METADATA_FILENAME = 'metadata.xml';

// Type for API errors with potential authorization properties
interface ApiError extends Error {
  isUnauthorized?: boolean;
  status?: number;
}

const sharedInputProps: Partial<InputProps> = {
  fullWidth: true,
  isCopyable: true,
};

const fetchSAMLMetadata = async () => {
  const data = await apiClient.get<SAMLMetadata>(samlQueryKeys.endpoints.metadata());
  return {
    recipient: data?.recipient ?? '',
    audience: data?.audience ?? '',
  };
};

const EMPTY_PROVIDER_DEFAULTS: SAMLProviderFormDefaults = {
  provider_name: '',
  email_domain: '',
  metadata_url: '',
  metadata_file: '',
  attr_email: '',
  attr_first_name: '',
  attr_last_name: '',
  attr_roles: '',
};

// get_saml_provider 404s twice over: no provider row (first-time setup) and a row whose Cognito
// provider vanished, which it logs as an error. Rendering the second as an empty form would invite
// a blind overwrite, and only the detail text separates them — see issue #399 for the backend fix.
const UNCONFIGURED_PROVIDER_DETAIL = 'No SAML provider configured for email domain';

const isUnconfiguredProvider = (error: unknown): boolean => {
  const apiError = error as (ApiError & { data?: { detail?: string } }) | undefined;
  if (apiError?.status !== 404) return false;
  return (apiError.data?.detail ?? apiError.message ?? '').includes(UNCONFIGURED_PROVIDER_DETAIL);
};

const fetchSAMLProvider = async (): Promise<SAMLProviderFormDefaults> => {
  // The wire response nests attribute mapping under `attribute_mapping`, not as flat `attr_*`
  // fields — reading `data.attr_email` etc. directly silently produced '' for every field,
  // which wiped the user's attribute mapping on the refetch that follows a save.
  let data: SAMLProviderGetResponse | null;
  try {
    data = await apiClient.get<SAMLProviderGetResponse>(samlQueryKeys.endpoints.provider());
  } catch (error) {
    if (isUnconfiguredProvider(error)) return EMPTY_PROVIDER_DEFAULTS;
    throw error;
  }
  return {
    provider_name: data?.provider_name ?? '',
    email_domain: data?.email_domain ?? '',
    metadata_url: data?.metadata_url ?? '',
    metadata_file: data?.metadata_file ?? '',
    attr_email: data?.attribute_mapping?.email ?? '',
    attr_first_name: data?.attribute_mapping?.first_name ?? '',
    attr_last_name: data?.attribute_mapping?.last_name ?? '',
    attr_roles: data?.attribute_mapping?.roles ?? '',
  };
};

const AuthPage: FC = () => {
  // Redirects if the session loses both super_user and org_admin in the selected org, or switches
  // to an org holding neither. Also gates render during auth resolution / a resolved denial (#396).
  const gate = useOrgAdminRenderGate(adminRoles);
  const queryClient = useQueryClient();

  const [showOptions, setShowOptions] = useState<boolean>(false);

  const onCloseHandler = () => setShowOptions(false);
  const { t } = useTranslation('auth');
  // Both are `undefined` whenever the loader short-circuited (see the route's own comment), so a
  // blank shape only ever reaches these 24h-staleTime queries from a real fetch that found no
  // provider. The gate.hasAccess arm keeps a denied session from hydrating a cache it can't read.
  const { samlMetadata: samlMetadataFromLoader, providerDefaults: providerDefaultsFromLoader } = useLoaderData({
    from: '/auth/',
  }) as {
    samlMetadata?: SAMLMetadata;
    providerDefaults?: SAMLProviderFormDefaults;
  };
  const samlMetadataInitial = gate.hasAccess ? samlMetadataFromLoader : undefined;
  const providerDefaultsInitial = gate.hasAccess ? providerDefaultsFromLoader : undefined;

  const navigate = useNavigate();

  // SAML Metadata Query with manual refetch and select
  const {
    data: samlMetadata,
    isLoading: loadingMetadata,
    error: errorMetadata,
    refetch: refetchMetadata,
    isFetching: isFetchingMetadata,
  } = useQuery<SAMLMetadata>({
    queryKey: samlQueryKeys.metadata(),
    queryFn: fetchSAMLMetadata,
    initialData: samlMetadataInitial,
    enabled: gate.hasAccess,
    staleTime: 24 * 60 * 60 * 1000, // 1 day
    retry: (failureCount, error) => {
      const apiError = error as ApiError;
      if (apiError?.isUnauthorized || error?.name === 'UnauthorizedError' || apiError?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
    select: data => ({
      recipient: data.recipient,
      audience: data.audience,
    }),
  });

  const {
    data: provider,
    isLoading: loadingProvider,
    error: errorProvider,
    refetch: refetchProvider,
    isFetching: isFetchingProvider,
  } = useQuery<SAMLProviderFormDefaults>({
    queryKey: samlQueryKeys.provider(),
    queryFn: fetchSAMLProvider,
    initialData: providerDefaultsInitial,
    enabled: gate.hasAccess,
    staleTime: 24 * 60 * 60 * 1000, // 1 day
    retry: (failureCount, error) => {
      const apiError = error as ApiError;
      if (apiError?.isUnauthorized || error?.name === 'UnauthorizedError' || apiError?.status === 403) {
        return false;
      }
      // A 404 here is a verdict, not a blip — retrying only delays surfacing the drift case.
      if (apiError?.status === 404) return false;
      return failureCount < 3;
    },
    select: data => ({
      provider_name: data.provider_name ?? '',
      email_domain: data.email_domain ?? '',
      metadata_url: data.metadata_url ?? '',
      metadata_file: data.metadata_file ?? '',
      attr_email: data.attr_email ?? '',
      attr_first_name: data.attr_first_name ?? '',
      attr_last_name: data.attr_last_name ?? '',
      attr_roles: data.attr_roles ?? '',
    }),
  });

  useEffect(() => {
    // Only 403 should land on /unauthorized. 401 is handled globally by
    // ApiClient, which navigates to /login after clearing client state.
    if (isForbiddenError(errorMetadata) || isForbiddenError(errorProvider)) {
      navigate({ to: '/unauthorized' });
    }
  }, [errorMetadata, errorProvider, navigate]);

  // Memoize providerDefaults for form default values
  const providerDefaults = useMemo<SamlProviderFormValues>(
    () => ({
      provider_name: provider?.provider_name ?? '',
      email_domain: provider?.email_domain ?? '',
      metadata_url: provider?.metadata_url ?? '',
      metadata_file: provider?.metadata_file ?? '',
      attr_email: provider?.attr_email ?? '',
      attr_first_name: provider?.attr_first_name ?? '',
      attr_last_name: provider?.attr_last_name ?? '',
      attr_roles: provider?.attr_roles ?? '',
    }),
    [provider],
  );

  const samlSchema = useMemo(() => buildSamlSchema(t), [t]);

  const form = useForm({
    defaultValues: providerDefaults,
    onSubmit: async ({ value, formApi }) => {
      if (!value.metadata_url && !value.metadata_file) {
        toast.error(t('saml.metadata_required'));
        return;
      }
      const result = samlSchema.safeParse(value);

      if (!result.success) {
        // Gather all field errors from formApi
        const allErrors = [
          ...new Set([
            ...Object.entries(formApi.state.fieldMeta)
              .map(([field, meta]) => (meta?.errors?.[0] ? `${field}: ${meta.errors[0]}` : null))
              .filter((x): x is string => x !== null),
            ...(result.error
              ? result.error.issues.map((issue: z.core.$ZodIssue) => `${issue.path.join('.')}: ${issue.message}`)
              : []),
          ]),
        ];

        // Show all errors in a toast
        toast.error(
          <Stack gap={1} orientation="vertical">
            <Text as="strong">{t('saml.please_fix_errors')}</Text>
            {allErrors.map(err => (
              <Text as="span" key={String(err)} size="xs">
                {err}
              </Text>
            ))}
          </Stack>,
        );
        return;
      }

      const formData = new FormData();
      formData.append('provider_name', value.provider_name);
      formData.append('email_domain', value.email_domain);

      formData.append('attr_email', value.attr_email);
      formData.append('attr_first_name', value.attr_first_name);
      formData.append('attr_last_name', value.attr_last_name);
      formData.append('attr_roles', value.attr_roles);

      if (value.metadata_url) {
        formData.append('metadata_url', value.metadata_url);
      }
      // A provider loaded with a file arrives as XML text, and the backend requires one of the two
      // metadata sources on *every* update — including one that changes neither — so re-post that
      // text as a file part rather than dropping the field and taking a 400.
      if (value.metadata_file instanceof File) {
        formData.append('metadata_file', value.metadata_file);
      } else if (value.metadata_file) {
        formData.append(
          'metadata_file',
          new File([value.metadata_file], EXISTING_METADATA_FILENAME, { type: 'text/xml' }),
        );
      }

      // Show loading toast and keep its ID
      const toastId = toast.loading(t('saml.updating_config'));

      try {
        // Use canonical apiClient to perform the POST. apiClient handles credentials and
        // will skip default headers for FormData uploads.
        await apiClient.post('/auth/saml/provider', formData, { skipDefaultHeaders: true });
        toast.success(t('saml.config_success'), { id: toastId });
        // Seed what was just saved before refetching: the GET scopes to the caller's own email
        // domain, so configuring any other domain 404s, and an unseeded refetch would resolve to
        // the unconfigured defaults and blank the form the admin just filled in.
        queryClient.setQueryData(samlQueryKeys.provider(), value);
        // The refetch is only authoritative for the caller's own domain. Saving any other domain
        // 404s into the unconfigured defaults, which would overwrite the seed and blank the form
        // the admin just submitted — so discard a result that isn't for what was saved.
        const { data: refetched } = await refetchProvider();
        if (refetched && refetched.email_domain !== value.email_domain) {
          queryClient.setQueryData(samlQueryKeys.provider(), value);
        }
        return undefined;
      } catch (error: unknown) {
        // `||`, not `??`: a wrapper that builds its message from an empty response body yields '',
        // which is as useless to the admin as a missing message and must still fall back.
        const message = (error as Error)?.message?.trim() || t('saml.config_error');
        toast.error(message, { id: toastId });
        throw error;
      }
    },
    validators: {
      onChange: ({ value }) => {
        const result = samlSchema.safeParse(value);
        if (!result.success) {
          return result.error.message;
        }
        return undefined;
      },
    },
  });

  const formRef = useRef(form);

  // Update formRef when form changes
  useEffect(() => {
    formRef.current = form;
  }, [form]);

  // Reset form when provider changes
  useEffect(() => {
    formRef.current.reset(providerDefaults);
  }, [providerDefaults]);

  /** A newly picked file shows its own name; a provider loaded with one has only XML content, which
   *  is a document, not a label — rendering it raw put the whole thing on the button. */
  const metadataFileLabel = useCallback(
    (value: SamlMetadataFile) => {
      if (value instanceof File) return t('saml.selected_metadata_file', { file: value.name });
      return value ? t('saml.existing_metadata_file') : t('saml.upload_metadata_xml');
    },
    [t],
  );

  // Disable submit if loading or error
  const disableSubmit = loadingMetadata || loadingProvider || errorMetadata || errorProvider;

  // Held until the provider query resolves once, not just until the gate authorizes: with the
  // loader no longer seeding initialData, the form would otherwise paint blank for a round trip
  // and then have anything typed into it wiped by the reset effect when the real data lands.
  const awaitingProvider = loadingProvider && provider === undefined;

  if (gate.status !== 'authorized' || awaitingProvider) {
    return (
      <RouteAccessGate
        deniedLabel={t('checking_access')}
        pendingLabel={t('saml.loading_settings')}
        status={gate.status === 'authorized' ? 'pending' : gate.status}
      />
    );
  }

  return (
    <form
      className={styles.form}
      onSubmit={async e => {
        e.preventDefault();
        e.stopPropagation();

        await form.handleSubmit();
      }}
    >
      <Stack fullHeight fullWidth gap={6} orientation="vertical">
        {/* disableSubmit blocks submission on a fetch failure; without this the admin would see a
            permanently greyed-out Save and nothing explaining it (the Refresh buttons retry). A 403
            is excluded because the effect above is already navigating away from it. */}
        {(errorMetadata || errorProvider) && !isForbiddenError(errorMetadata) && !isForbiddenError(errorProvider) && (
          <Alert data-testid="saml-load-error" mode="block" variant="error">
            {t('saml.error_loading_settings')}
          </Alert>
        )}
        <Tabs className={styles.tabs} data-testid={'auth-tabs'}>
          <TabList aria-label={t('saml.aria_tablist_auth_settings')} className={styles.tabList}>
            <Tab data-testid="tab-saml" id="saml">
              <Stack alignItems="center" as="span" gap={1} orientation="horizontal">
                <Lock size={14} />
                {t('saml.saml_tab')}
              </Stack>
            </Tab>
          </TabList>

          <TabPanel
            className={clsx(
              styles.tabContents,
              getStackClassName('displayflex'),
              getStackClassName('gap4'),
              getStackClassName('vertical'),
              scrollbars,
            )}
            data-testid="tabpanel-saml"
            id="saml"
          >
            <Popover
              isOpen={showOptions}
              data-testid="auth-popover"
              content={
                <Group orientation="vertical">
                  <Button
                    data-testid={'refresh-metadata-btn'}
                    onPress={isFetchingMetadata ? noop : () => refetchMetadata()}
                    size="sm"
                    visual={isFetchingMetadata ? 'primary' : 'default'}
                  >
                    {isFetchingMetadata ? t('saml.refreshing_metadata') : t('saml.refresh_metadata')}
                  </Button>
                  <Button
                    data-testid={'refresh-provider-btn'}
                    onPress={isFetchingProvider ? noop : () => refetchProvider()}
                    size="sm"
                    visual={isFetchingProvider ? 'primary' : 'default'}
                  >
                    {isFetchingProvider ? t('saml.refreshing_provider') : t('saml.refresh_provider')}
                  </Button>
                </Group>
              }
              onClickOutside={onCloseHandler}
              positions={['bottom']}
            >
              <Button
                className={styles.options}
                data-testid="saml-options-btn"
                onPress={() => setShowOptions(s => !s)}
                visual="unstyled"
              >
                <Tooltip
                  content={t('saml.options_tooltip')}
                  hoverOptions={{ delay: 1000 }}
                  isDisabled={showOptions}
                  placement="left-start"
                  trigger={<Cog color="white" size={24} />}
                />
              </Button>
            </Popover>
            <FormSectionTitle
              label={t('saml.portal_parameters')}
              tooltip={t('saml.portal_parameters_tooltip')}
              aria-label={t('saml.aria_portal_parameters')}
            />
            <Stack fullWidth gap={4} orientation="vertical">
              <table width="100%">
                <tbody>
                  <tr>
                    <td align="left">{t('saml.recipient')}</td>
                    <td align="left">
                      <CopyText id={'saml-recipient'} value={samlMetadata?.recipient} />
                    </td>
                  </tr>
                  <tr>
                    <td align="left">{t('saml.audience')}</td>
                    <td align="left">
                      <CopyText id={'saml-audience'} value={samlMetadata?.audience} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </Stack>

            <FormSectionTitle
              label={t('saml.identity_provider')}
              tooltip={t('saml.identity_provider_tooltip')}
              aria-label={t('saml.aria_identity_provider')}
            />

            <Stack className={styles.formSection} fullWidth gap={6} orientation="vertical">
              <form.Field
                name="provider_name"
                validators={{
                  onChange: ({ value }) => {
                    if (!value) return t('saml.provider_name_required');
                    if (value.length > COGNITO_PROVIDER_NAME_MAX_LENGTH) return t('saml.provider_name_too_long');
                    return undefined;
                  },
                }}
              >
                {field => (
                  <>
                    <Input
                      {...sharedInputProps}
                      data-testid="input-provider_name"
                      label={{
                        children: t('saml.provider_name_label'),
                        htmlFor: 'provider_name',
                        id: 'label-provider_name',
                      }}
                      id="provider_name"
                      value={field.state.value || ''}
                      onChange={e => field.handleChange(e.target.value)}
                      required
                      aria-describedby={field.state.meta.errors?.[0] ? 'provider_name-error' : undefined}
                    />
                    {field.state.meta.errors?.[0] && (
                      <Text as="span" id="provider_name-error">
                        {field.state.meta.errors[0]}
                      </Text>
                    )}
                  </>
                )}
              </form.Field>
              <form.Field
                name="email_domain"
                validators={{
                  onChange: ({ value }) => (!value ? t('saml.email_domain_required') : undefined),
                }}
              >
                {field => (
                  <>
                    <Input
                      {...sharedInputProps}
                      data-testid="input-email_domain"
                      id="email_domain"
                      label={{
                        children: t('saml.email_domain_label'),
                        htmlFor: 'email_domain',
                        id: 'label-email_domain',
                      }}
                      value={field.state.value || ''}
                      onChange={e => field.handleChange(e.target.value)}
                      required
                      aria-describedby={field.state.meta.errors?.[0] ? 'email_domain-error' : undefined}
                    />
                    {field.state.meta.errors?.[0] && (
                      <Text as="span" id="email_domain-error">
                        {field.state.meta.errors[0]}
                      </Text>
                    )}
                  </>
                )}
              </form.Field>

              <Stack alignItems="center" fullWidth gap={4} justifyContent="space-between" orientation="horizontal">
                <form.Field
                  name="metadata_url"
                  validators={{
                    onChangeListenTo: ['metadata_file'],
                    // All three metadata_url rules surface here: this is the only one of the group
                    // rendered with an error slot, and the whole-form validator's flat string
                    // reaches none.
                    onChange: ({ value, fieldApi }) => {
                      const file = fieldApi.form.getFieldValue('metadata_file');
                      if (value && file) return t('saml.metadata_exclusive');
                      if (!value && !file) return t('saml.metadata_required');
                      if (value && !isValidUrl(value)) return t('saml.metadata_url_invalid');
                      return undefined;
                    },
                  }}
                >
                  {field => (
                    <>
                      <Input
                        {...sharedInputProps}
                        data-testid="input-metadata_url"
                        id="metadata_url"
                        label={{
                          children: t('saml.metadata_url_label'),
                          htmlFor: 'metadata_url',
                          id: 'label-metadata_url',
                        }}
                        onChange={e => field.handleChange(e.target.value)}
                        value={field.state.value || ''}
                        aria-describedby={field.state.meta.errors?.[0] ? 'metadata_url-error' : undefined}
                      />
                      {field.state.meta.errors?.[0] && (
                        <Text as="span" id="metadata_url-error">
                          {field.state.meta.errors[0]}
                        </Text>
                      )}
                    </>
                  )}
                </form.Field>

                <Text as="span">{t('saml.or')}</Text>

                {/* A real field, not just a form value: onChangeListenTo only notifies linked
                    fields from a mounted field's own change validation, so the URL field could
                    never see an upload land while this was set through form.setFieldValue. */}
                <form.Field name="metadata_file">
                  {field => (
                    <>
                      <Upload
                        acceptedFileTypes={['.xml']}
                        buttonProps={{
                          style: { minWidth: 400 },
                          visual: 'primary',
                        }}
                        buttonText={metadataFileLabel(field.state.value)}
                        data-testid="input-metadata_file"
                        onError={error => {
                          console.error('SAML metadata file rejected', error);
                          toast.error(t('saml.metadata_file_rejected'));
                        }}
                        // Upload hands back a FileList, which is neither an array nor a File — the
                        // two shapes this used to test for — so every selection used to fall
                        // through and clear the field.
                        onSelect={files => field.handleChange(files?.[0] ?? '')}
                      />
                      {field.state.value && (
                        <Button
                          data-testid={'clear-file-btn'}
                          onPress={() => field.handleChange('')}
                          size="xs"
                          visual="unstyled"
                        >
                          {t('saml.clear_file')}
                        </Button>
                      )}
                    </>
                  )}
                </form.Field>
              </Stack>
            </Stack>

            <FormSectionTitle
              label={t('saml.user_attribute_mapping')}
              tooltip={t('saml.user_attribute_mapping_tooltip')}
              aria-label={t('saml.aria_user_attribute_mapping')}
            />

            <Stack className={styles.formSection} fullWidth gap={6} orientation="vertical">
              <form.Field
                name="attr_email"
                validators={{
                  onChange: ({ value }) => (!value ? t('saml.attr_email_required') : undefined),
                }}
              >
                {field => (
                  <>
                    <Input
                      {...sharedInputProps}
                      data-testid="input-attr_email"
                      id="attr_email"
                      label={{
                        children: t('saml.attr_email_label'),
                        htmlFor: 'attr_email',
                        id: 'label-attr_email',
                      }}
                      value={field.state.value || ''}
                      onChange={e => field.handleChange(e.target.value)}
                      required
                      aria-describedby={field.state.meta.errors?.[0] ? 'attr_email-error' : undefined}
                    />
                    {field.state.meta.errors?.[0] && (
                      <Text as="span" id="attr_email-error">
                        {field.state.meta.errors[0]}
                      </Text>
                    )}
                  </>
                )}
              </form.Field>
              <form.Field
                name="attr_first_name"
                validators={{
                  onChange: ({ value }) => (!value ? t('saml.attr_first_name_required') : undefined),
                }}
              >
                {field => (
                  <>
                    <Input
                      {...sharedInputProps}
                      data-testid="input-attr_first_name"
                      id="attr_first_name"
                      label={{
                        children: t('saml.attr_first_name_label'),
                        htmlFor: 'attr_first_name',
                        id: 'label-attr_first_name',
                      }}
                      value={field.state.value || ''}
                      onChange={e => field.handleChange(e.target.value)}
                      required
                      aria-describedby={field.state.meta.errors?.[0] ? 'attr_first_name-error' : undefined}
                    />
                    {field.state.meta.errors?.[0] && (
                      <Text as="span" id="attr_first_name-error">
                        {field.state.meta.errors[0]}
                      </Text>
                    )}
                  </>
                )}
              </form.Field>

              <form.Field
                name="attr_last_name"
                validators={{
                  onChange: ({ value }) => (!value ? t('saml.attr_last_name_required') : undefined),
                }}
              >
                {field => (
                  <>
                    <Input
                      {...sharedInputProps}
                      data-testid="input-attr_last_name"
                      id="attr_last_name"
                      label={{
                        children: t('saml.attr_last_name_label'),
                        htmlFor: 'attr_last_name',
                        id: 'label-attr_last_name',
                      }}
                      value={field.state.value || ''}
                      onChange={e => field.handleChange(e.target.value)}
                      required
                      aria-describedby={field.state.meta.errors?.[0] ? 'attr_last_name-error' : undefined}
                    />
                    {field.state.meta.errors?.[0] && (
                      <Text as="span" id="attr_last_name-error">
                        {field.state.meta.errors[0]}
                      </Text>
                    )}
                  </>
                )}
              </form.Field>
              <form.Field
                name="attr_roles"
                validators={{
                  onChange: ({ value }) => (!value ? t('saml.attr_roles_required') : undefined),
                }}
              >
                {field => (
                  <>
                    <Input
                      {...sharedInputProps}
                      data-testid="input-attr_roles"
                      id="attr_roles"
                      label={{
                        children: t('saml.attr_roles_label'),
                        htmlFor: 'attr_roles',
                        id: 'label-attr_roles',
                      }}
                      value={field.state.value || ''}
                      onChange={e => field.handleChange(e.target.value)}
                      required
                      aria-describedby={field.state.meta.errors?.[0] ? 'attr_roles-error' : undefined}
                    />
                    {field.state.meta.errors?.[0] && (
                      <Text as="span" id="attr_roles-error">
                        {field.state.meta.errors[0]}
                      </Text>
                    )}
                  </>
                )}
              </form.Field>
            </Stack>

            <form.Subscribe
              selector={state => [
                state.canSubmit,
                state.isSubmitting,
                state.isDirty,
                state.isValid,
                state.values,
                state.errors,
              ]}
            >
              {([canSubmit, isSubmitting, isDirty, isValid]) => (
                <Stack fullWidth>
                  <Button
                    data-testid={'configure-submit-btn'}
                    isDisabled={!!disableSubmit || !canSubmit || !isDirty || !isValid}
                    size="lg"
                    type="submit"
                    visual={isSubmitting ? 'success' : 'primary'}
                  >
                    {isSubmitting ? t('saml.configuring') : t('saml.configure_provider')}
                  </Button>
                </Stack>
              )}
            </form.Subscribe>
          </TabPanel>
        </Tabs>
      </Stack>
    </form>
  );
};

export default AuthPage;
